// FormAccessControl.js
// Complete working component with Desk-based Forms

import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Card,
  Typography,
  Checkbox,
  message,
  Space,
  Table,
  Avatar,
  Row,
  Col,
  Spin,
  Alert,
  Modal,
  Tag,
  Button,
  List,
  Divider,
  Badge,
  Input,
  Tabs,
  Form,
  Select,
  Descriptions,
  Tooltip,
} from "antd";
import {
  ApiOutlined,
  EnvironmentOutlined,
  BankOutlined,
  GlobalOutlined,
  CheckCircleOutlined,
  SearchOutlined,
  DesktopOutlined,
  FilterOutlined,
  AppstoreOutlined,
  FileTextOutlined,
  PlusOutlined,
  EyeOutlined,
  CloseOutlined,
  ReloadOutlined,
  UserOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";

const { Title, Text } = Typography;
const { Search } = Input;
const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_BASE = `${API_BASE_URL}/api`;

const FormAccessControl = ({
  language = 'english',
  permissions = {},
  onPermissionsChange,
  formId,
  onFormSave,
}) => {
  // Static roles mapping - EXCLUDING ULB_CHIEF_OFFICIER (ID: 7)
  const staticRoles = useMemo(() => [
    { id: 1, name: 'ULB', description: 'ULB Role' },
    { id: 2, name: 'DISTRICT_ADMIN', description: 'District Admin Role' },
    { id: 3, name: 'DIVISIONAL_OFFICER', description: 'Divisional Officer Role' },
    { id: 100, name: 'Desk', isHardcoded: true, description: 'Desk Role' }
    // Role ID: 7 (ULB_CHIEF_OFFICIER) completely removed
  ], []);

  // ========== STATE VARIABLES ==========
  const [roles] = useState(staticRoles);
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Form creation states
  const [showFormModal, setShowFormModal] = useState(false);
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [selectedDeskForForm, setSelectedDeskForForm] = useState(null);
  const [creatingForm, setCreatingForm] = useState(false);
  const [desksList, setDesksList] = useState([]);
  const [existingForms, setExistingForms] = useState([]);
  const [showFormPreview, setShowFormPreview] = useState(false);
  const [previewForm, setPreviewForm] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  
  // Modal states for Desk selection
  const [showModal, setShowModal] = useState(false);
  const [currentRole, setCurrentRole] = useState(null);
  const [modalType, setModalType] = useState(null);
  const [selectedDesks, setSelectedDesks] = useState([]);
  const deskOptions = [1, 2, 3, 4, 5, 6, 7, 8];
  
  // ULB states
  const [divisions, setDivisions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [ulbs, setUlbs] = useState([]);
  const [loadingError, setLoadingError] = useState(null);
  const [divisionMap, setDivisionMap] = useState({});
  const [districtMap, setDistrictMap] = useState({});
  const [searchText, setSearchText] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const listContainerRef = useRef(null);

  // ========== INITIALIZATION ==========
  useEffect(() => {
    const user = sessionStorage.getItem('user');
    if (user) {
      try {
        const userData = JSON.parse(user);
        setCurrentUser(userData);
        console.log("👤 Current user:", userData);
      } catch (e) {
        console.error("Error parsing user:", e);
      }
    }
    fetchDesksList();
  }, []);

  // ========== DESK AND FORM FUNCTIONS ==========
  
  // Fetch all desks - Silent fail, don't show error message
  const fetchDesksList = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      
      const response = await axios.get(`${API_BASE}/access/desks`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-user-id': user?.id || 1,
          'x-user-name': user?.name || 'System',
        }
      });
      
      if (response.data.success) {
        setDesksList(response.data.desks);
        console.log("✅ Desks loaded:", response.data.desks);
      }
    } catch (error) {
      // Silent fail - don't show error message
      console.error('Fetch desks error:', error);
      // Set empty desks list to avoid any errors
      setDesksList([]);
    }
  };

  // Create new form
  const handleCreateForm = async () => {
    console.log("🔍 DEBUG - Creating form:", {
      title: formTitle,
      description: formDescription,
      selectedDesk: selectedDeskForForm
    });
    
    if (!formTitle || formTitle.trim() === '') {
      message.error('Please enter form title');
      return;
    }
    
    let deskId = selectedDeskForForm;
    if (!deskId) {
      console.warn("⚠️ No desk selected! Defaulting to 1");
      deskId = 1;
    }
    
    deskId = parseInt(deskId);
    if (isNaN(deskId)) {
      deskId = 1;
    }
    
    const payload = {
      title: formTitle.trim(),
      description: formDescription || '',
      assigned_desk: deskId
    };
    
    console.log("📦 Sending payload:", payload);
    
    setCreatingForm(true);
    try {
      const token = sessionStorage.getItem('token');
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      
      const response = await axios.post(
        `${API_BASE}/access/form/create`,
        payload,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'x-user-id': user?.id || 1,
            'x-user-name': user?.name || 'System',
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log("✅ Response:", response.data);
      
      if (response.data.success) {
        message.success('Form created successfully!');
        setShowFormModal(false);
        setFormTitle('');
        setFormDescription('');
        setSelectedDeskForForm(null);
        
        if (onFormSave) {
          onFormSave(response.data);
        }
      } else {
        message.error(response.data.error || 'Failed to create form');
      }
    } catch (error) {
      console.error('Create form error:', error);
      message.error(error.response?.data?.error || 'Failed to create form');
    } finally {
      setCreatingForm(false);
    }
  };

  // Update form status
  const handleUpdateFormStatus = async (formId, status) => {
    setUpdatingStatus(true);
    try {
      const token = sessionStorage.getItem('token');
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      
      const response = await axios.put(
        `${API_BASE}/access/form/${formId}/status`,
        { status: status },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'x-user-id': user?.id || 1,
            'x-user-name': user?.name || 'System',
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data.success) {
        message.success(`Form ${status} successfully!`);
        setShowFormPreview(false);
      } else {
        message.error(response.data.error || 'Failed to update status');
      }
    } catch (error) {
      console.error('Update status error:', error);
      message.error(error.response?.data?.error || 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Delete form
  const handleDeleteForm = async (formId) => {
    try {
      const token = sessionStorage.getItem('token');
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      
      const response = await axios.delete(`${API_BASE}/access/form/${formId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-user-id': user?.id || 1,
          'x-user-name': user?.name || 'System',
        }
      });
      
      if (response.data.success) {
        message.success('Form deleted successfully!');
      } else {
        message.error(response.data.error || 'Failed to delete form');
      }
    } catch (error) {
      console.error('Delete form error:', error);
      message.error(error.response?.data?.error || 'Failed to delete form');
    }
  };

  // ========== DESK SELECTION FUNCTIONS ==========
  const openDeskModal = (role) => {
    setCurrentRole(role);
    setModalType('desk');
    const rolePermissions = permissions[role.id] || {};
    // Support both possible key names
    const savedDesks = rolePermissions.selectedDesks || rolePermissions.selectedItems || [];
    setSelectedDesks(savedDesks);
    setShowModal(true);
    console.log(`📌 Opening desk modal for role ${role.id}, saved desks:`, savedDesks);
  };

  const toggleDesk = (desk) => {
    if (selectedDesks.includes(desk)) {
      setSelectedDesks(selectedDesks.filter(d => d !== desk));
    } else {
      setSelectedDesks([...selectedDesks, desk]);
    }
  };

  const selectAllDesks = () => setSelectedDesks([...deskOptions]);
  const clearAllDesks = () => setSelectedDesks([]);

  const saveDeskSelections = () => {
    if (!currentRole) return;
    
    const savedDesks = [...selectedDesks];
    
    const updatedPermissions = {
      ...permissions,
      [currentRole.id]: {
        can_access: savedDesks.length > 0,
        can_view: savedDesks.length > 0,
        can_edit: savedDesks.length > 0,
        can_submit: savedDesks.length > 0,
        selectedDesks: savedDesks,
        selectedDesksCount: savedDesks.length,
        modalType: 'desk',
        roleId: currentRole.id,
        roleName: currentRole.name,
        updatedAt: new Date().toISOString()
      }
    };
    
    if (onPermissionsChange) {
      onPermissionsChange(updatedPermissions);
    }
    
    setShowModal(false);
    message.success(`${savedDesks.length} desk(s) selected`);
  };

  // ========== LOAD USERS BY ROLE (DISTRICT ADMIN & DIVISIONAL OFFICER) ==========
  const loadUsersByRole = async (roleId) => {
    try {
      setUlbs([]);
      
      let apiUrl = '';
      if (roleId === 2) {
        apiUrl = `${API_BASE}/admin/district-admins`;
      } else if (roleId === 3) {
        apiUrl = `${API_BASE}/admin/divisional-officers`;
      }
      
      const response = await axios.get(apiUrl);
      
      if (response.data?.success && Array.isArray(response.data?.users)) {
        const users = response.data.users.map(user => ({
          id: user.id,
          name: user.full_name || user.username,
          ulb_name: user.username,
          district_id: user.district_id,
          division_id: user.division_id,
          class: 'User',
          ulb_type: roleId === 2 ? 'District Admin' : 'Divisional Officer',
          username: user.username,
          email: user.email,
          full_name: user.full_name,
          designation: user.designation,
          created_at: user.created_at,
          phone_no: user.phone_no,
          status: user.status
        }));
        setUlbs(users);
        console.log(`✅ Loaded ${users.length} users for role ${roleId}`);
      }
    } catch (error) {
      // console.error("Failed to load users:", error);
      setLoadingError(error.message);
    }
  };

  // ========== ULB FUNCTIONS ==========
  const getDivisionName = (divisionId) => {
    if (!divisionId) return 'N/A';
    return divisionMap[divisionId] || `Division ${divisionId}`;
  };

  const getDistrictName = (districtId) => {
    if (!districtId) return 'N/A';
    return districtMap[districtId] || `District ${districtId}`;
  };

  const getUniqueClasses = () => {
    const classes = new Set();
    ulbs.forEach(ulb => { if (ulb.class) classes.add(ulb.class.toString()); });
    return Array.from(classes).sort();
  };

  const getUniqueTypes = () => {
    const types = new Set();
    ulbs.forEach(ulb => { if (ulb.ulb_type) types.add(ulb.ulb_type); });
    return Array.from(types).sort();
  };

  const getClassColor = (className) => {
    const classStr = String(className).toUpperCase();
    if (classStr.includes('A')) return 'green';
    if (classStr.includes('B')) return 'blue';
    if (classStr.includes('C')) return 'orange';
    if (classStr.includes('NP')) return 'purple';
    return 'default';
  };

  const getTypeColor = (typeName) => {
    const typeStr = String(typeName).toUpperCase();
    if (typeStr.includes('MUNICIPAL')) return 'cyan';
    if (typeStr.includes('CORPORATION')) return 'volcano';
    if (typeStr.includes('COUNCIL')) return 'geekblue';
    return 'default';
  };

  const toggleClass = (className) => {
    const classStr = className.toString();
    if (selectedClasses.includes(classStr)) {
      setSelectedClasses(selectedClasses.filter(c => c !== classStr));
    } else {
      setSelectedClasses([...selectedClasses, classStr]);
    }
  };

  const toggleType = (typeName) => {
    if (selectedTypes.includes(typeName)) {
      setSelectedTypes(selectedTypes.filter(t => t !== typeName));
    } else {
      setSelectedTypes([...selectedTypes, typeName]);
    }
  };

  const selectAllClasses = () => setSelectedClasses(getUniqueClasses());
  const clearAllClasses = () => setSelectedClasses([]);
  const selectAllTypes = () => setSelectedTypes(getUniqueTypes());
  const clearAllTypes = () => setSelectedTypes([]);

  const selectAll = () => setSelectedItems(ulbs.map(u => u.id));
  const selectAllFiltered = () => {
    const filteredIds = filteredData.map(item => item.id);
    setSelectedItems([...new Set([...selectedItems, ...filteredIds])]);
  };
  const clearAll = () => setSelectedItems([]);

  const toggleItem = (itemId) => {
    if (selectedItems.includes(itemId)) {
      setSelectedItems(selectedItems.filter(id => id !== itemId));
    } else {
      setSelectedItems([...selectedItems, itemId]);
    }
  };

  // UPDATED: saveUlbSelections - Works for ULB, District Admin, and Divisional Officer
  const saveUlbSelections = () => {
    if (!currentRole) return;
    
    const selectedIds = [...selectedItems];
    
    // Determine the label for message
    let label = "ULBs";
    if (currentRole.id === 2) label = "District Admins";
    else if (currentRole.id === 3) label = "Divisional Officers";
    
    const updatedPermissions = {
      ...permissions,
      [currentRole.id]: {
        can_access: selectedIds.length > 0,
        can_view: selectedIds.length > 0,
        can_edit: selectedIds.length > 0,
        can_submit: selectedIds.length > 0,
        selectedIds: selectedIds,
        selectedCount: selectedIds.length,
        modalType: 'ulb',
        roleId: currentRole.id,
        roleName: currentRole.name,
        updatedAt: new Date().toISOString()
      }
    };
    
    if (onPermissionsChange) {
      onPermissionsChange(updatedPermissions);
    }
    
    setShowModal(false);
    message.success(`${selectedIds.length} ${label} selected`);
  };

  // ========== OPEN MODAL WITH CONDITIONAL DATA LOADING ==========
  const openUlbModal = async (role) => {
    setCurrentRole(role);
    setSearchText('');
    setActiveTab('all');
    setSelectedClasses([]);
    setSelectedTypes([]);
    setModalType('ulb');
    setLoadingData(true);
    setLoadingError(null);
    setShowModal(true);
    
    try {
      const rolePermissions = permissions[role.id] || {};
      // Support both possible key names for saved selections
      const savedIds = rolePermissions.selectedIds || rolePermissions.selectedItems || [];
      
      // District Admin (id: 2) or Divisional Officer (id: 3)
      if (role.id === 2 || role.id === 3) {
        await loadUsersByRole(role.id);
      } else {
        // ULB (id: 1) - original logic
        await loadAllDivisions();
        await loadAllDistricts();
        await loadAllUlbs();
      }
      
      setSelectedItems(savedIds);
      console.log(`📌 Loaded ${savedIds.length} saved selections for role ${role.id}`);
    } catch (error) {
      console.error("Error loading data:", error);
      setLoadingError(error.message);
    } finally {
      setLoadingData(false);
    }
  };

  const loadAllDivisions = async () => {
    try {
      const statesRes = await axios.get(`${API_BASE}/meta/states`);
      const states = statesRes.data?.data || [];
      let allDivisions = [];
      for (const state of states) {
        const res = await axios.get(`${API_BASE}/meta/divisions?state_id=${state.id}`);
        if (res.data?.success && res.data?.data) {
          allDivisions = [...allDivisions, ...res.data.data];
        }
      }
      setDivisions(allDivisions);
      const map = {};
      allDivisions.forEach(div => { map[div.id] = div.name || `Division ${div.id}`; });
      setDivisionMap(map);
    } catch (error) {
      // console.error("Failed to load divisions:", error);
    }
  };

  const loadAllDistricts = async () => {
    try {
      const statesRes = await axios.get(`${API_BASE}/meta/states`);
      const states = statesRes.data?.data || [];
      let allDistricts = [];
      for (const state of states) {
        const divisionsRes = await axios.get(`${API_BASE}/meta/divisions?state_id=${state.id}`);
        const divisions = divisionsRes.data?.data || [];
        for (const division of divisions) {
          const res = await axios.get(`${API_BASE}/meta/districts?division_id=${division.id}`);
          if (res.data?.success && res.data?.data) {
            allDistricts = [...allDistricts, ...res.data.data];
          }
        }
      }
      setDistricts(allDistricts);
      const map = {};
      allDistricts.forEach(dist => { map[dist.id] = dist.name || `District ${dist.id}`; });
      setDistrictMap(map);
      return allDistricts;
    } catch (error) {
      console.error("Failed to load districts:", error);
      return [];
    }
  };

 const loadAllUlbs = async () => {
  try {
    setUlbs([]);
    setLoadingError(null);
    const response = await axios.get(`${API_BASE}/meta/ulbs/all`);
    if (response.data?.success && Array.isArray(response.data?.data)) {
      const ulbsData = response.data.data;
      
      // ULBs ला class आणि type assign करा (name वरून infer)
      const processedUlbs = ulbsData.map(ulb => {
        const name = ulb.ulb_name || '';
        let ulb_type = 'Municipal Council';
        let class_type = 'B';
        
        // ULB Type infer
        if (name.includes('Municipal Corporation')) {
          ulb_type = 'Municipal Corporation';
          class_type = 'A';
        } else if (name.includes('Nagar Panchayat')) {
          ulb_type = 'Nagar Panchayat';
          class_type = 'NP';
        } else if (name.includes('Municipal Council') || name.includes('Muncipal Council')) {
          ulb_type = 'Municipal Council';
          // Class infer from name patterns
          if (name.includes('Achalpur') || name.includes('Amravati') || name.includes('Akola')) {
            class_type = 'A';
          } else if (name.includes('Balapur') || name.includes('Akot')) {
            class_type = 'B';
          } else if (name.includes('Patur') || name.includes('Telhara')) {
            class_type = 'C';
          } else {
            class_type = 'B'; // default
          }
        }
        
        return {
          id: ulb.id,
          name: ulb.ulb_name || `ULB ${ulb.id}`,
          ulb_name: ulb.ulb_name,
          district_id: ulb.district_id,
          division_id: ulb.division_id,
          class: class_type,
          ulb_type: ulb_type,
          hard: ulb.hard,
          available_preference: ulb.available_preference
        };
      });
      
      setUlbs(processedUlbs);
      console.log(`✅ Loaded ${processedUlbs.length} ULBs`);
      console.log("Sample ULB with class:", processedUlbs[0]);
    } else {
      setLoadingError("Invalid response format from server");
    }
  } catch (error) {
    console.error("Failed to load ULBs:", error);
    setLoadingError(error.response?.data?.error || error.message);
  }
};

  const filteredData = useMemo(() => {
    const data = ulbs;
    return data.filter(item => {
      if (selectedClasses.length > 0) {
        if (!item.class || !selectedClasses.includes(item.class.toString())) return false;
      }
      if (selectedTypes.length > 0) {
        if (!item.ulb_type || !selectedTypes.includes(item.ulb_type)) return false;
      }
      if (searchText.trim()) {
        const searchLower = searchText.toLowerCase().trim();
        const itemName = (item.ulb_name || item.name || '').toLowerCase();
        const divisionName = getDivisionName(item.division_id).toLowerCase();
        const districtName = getDistrictName(item.district_id).toLowerCase();
        return itemName.includes(searchLower) || divisionName.includes(searchLower) || districtName.includes(searchLower);
      }
      return true;
    });
  }, [ulbs, searchText, selectedClasses, selectedTypes, divisionMap, districtMap]);

  const getRoleColor = (roleId) => {
    if (roleId === 100) return "#722ed1";
    const roleColors = { 1: "#52c41a", 2: "#1890ff", 3: "#fa8c16" };
    return roleColors[roleId] || "#52c41a";
  };

  // UPDATED: getHierarchySummary - Shows correct labels for each role
  const getHierarchySummary = (roleId) => {
    const rolePerms = permissions[roleId];
    if (!rolePerms) return null;
    
    // Desk role
    if (roleId === 100) {
      const deskCount = rolePerms.selectedDesks?.length || 0;
      if (deskCount === 0) return null;
      return `${deskCount} Desk${deskCount > 1 ? 's' : ''}`;
    }
    
    // Get count - support both selectedIds and selectedItems
    const count = rolePerms.selectedIds?.length || rolePerms.selectedItems?.length || 0;
    if (count === 0) return null;
    
    // Show appropriate label based on role
    if (roleId === 2) {
      return `${count} District Admin${count > 1 ? 's' : ''}`;
    }
    if (roleId === 3) {
      return `${count} Divisional Officer${count > 1 ? 's' : ''}`;
    }
    // ULB role (id: 1)
    return `${count} ULB${count > 1 ? 's' : ''}`;
  };

  // Get modal title based on role
  const getModalTitle = () => {
    if (modalType === 'ulb') {
      if (currentRole?.id === 2) return "Select District Admin Users";
      if (currentRole?.id === 3) return "Select Divisional Officer Users";
      return "Select ULBs";
    }
    return "Select Desks";
  };

  return (
    <div style={{ background: "white", borderRadius: "8px", padding: "24px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
      
      {/* Header - WITHOUT Create New Form Button */}
      <div style={{ marginBottom: 24 }}>
        <div>
          <Title level={4} style={{ marginBottom: 0 }}>
            <ApiOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            Form Access Control
          </Title>
          <Text type="secondary">
            Configure which roles can access this form.
          </Text>
        </div>
      </div>

      {/* Roles Configuration Table - Only showing roles from staticRoles (ULB_CHIEF_OFFICIER excluded) */}
      <Table
        columns={[
          {
            title: "Role Name",
            dataIndex: "name",
            key: "name",
            render: (name, record) => (
              <Space>
                <Avatar size={32} style={{
                  backgroundColor: getRoleColor(record.id),
                  color: "white",
                  fontWeight: "bold"
                }}>
                  {name ? name.charAt(0).toUpperCase() : 'D'}
                </Avatar>
                <div>
                  <Text strong>{name || 'Unknown Role'}</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {record.isHardcoded ? '' : `ID: ${record.id}`}
                  </Text>
                </div>
              </Space>
            ),
          },
          {
            title: "Configure",
            key: "configure",
            width: 250,
            render: (_, record) => {
              if (record.id === 100) {
                const summary = getHierarchySummary(record.id);
                return (
                  <Space direction="vertical" size={2} style={{ width: '100%' }}>
                    <Button 
                      size="small" 
                      type="primary" 
                      ghost
                      onClick={() => openDeskModal(record)}
                      icon={<DesktopOutlined />}
                      style={{ backgroundColor: '#f9f0ff', borderColor: '#722ed1', color: '#722ed1' }}
                      block
                    >
                      Select Desks
                    </Button>
                    {summary && (
                      <Tag color="purple" style={{ fontSize: 11, width: '100%', textAlign: 'center' }}>
                        {summary}
                      </Tag>
                    )}
                  </Space>
                );
              }
              
              // Only show ULB selection for roles 1, 2, 3
              const showConfig = [1, 2, 3].includes(record.id);
              if (!showConfig) return null;
              
              const summary = getHierarchySummary(record.id);
              
              return (
                <Space direction="vertical" size={2} style={{ width: '100%' }}>
                  <Button 
                    size="small" 
                    type="primary" 
                    ghost
                    onClick={() => openUlbModal(record)}
                    icon={<EnvironmentOutlined />}
                    block
                  >
                    {record.id === 2 ? "Select District Admins" : record.id === 3 ? "Select Divisional Officers" : "Select ULBs"}
                  </Button>
                  {summary && (
                    <Tag color="blue" style={{ fontSize: 11, width: '100%', textAlign: 'center' }}>
                      {summary}
                    </Tag>
                  )}
                </Space>
              );
            }
          }
        ]}
        dataSource={roles}
        rowKey="id"
        pagination={false}
        size="middle"
      />

      <Row gutter={16} style={{ marginTop: 24 }}>
        <Col span={24}>
          <Card size="small">
            <Title level={5} style={{ color: "#52c41a" }}>
              ✓ Configured Roles ({Object.keys(permissions).length})
            </Title>
            <Text type="secondary">
              {Object.keys(permissions).length > 0 
                ? roles.filter(r => permissions[r.id] && (permissions[r.id].selectedIds?.length > 0 || permissions[r.id].selectedDesks?.length > 0)).map(r => r.name).join(", ") 
                : 'None configured'}
              {permissions[100] && permissions[100].selectedDesks?.length > 0 && ' Desk'}
            </Text>
          </Card>
        </Col>
      </Row>

      {/* ========== CREATE FORM MODAL ========== */}
      <Modal
        open={showFormModal}
        onCancel={() => {
          setShowFormModal(false);
          setFormTitle('');
          setFormDescription('');
          setSelectedDeskForForm(null);
        }}
        title="Create New Form"
        footer={[
          <Button key="cancel" onClick={() => setShowFormModal(false)}>Cancel</Button>,
          <Button key="submit" type="primary" onClick={handleCreateForm} loading={creatingForm}>
            Create Form
          </Button>,
        ]}
        width={600}
      >
        <Form layout="vertical">
          <Form.Item label="Form Title *" required>
            <Input
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              placeholder="Enter form title"
            />
          </Form.Item>

          <Form.Item label="Description">
            <TextArea
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              rows={3}
              placeholder="Enter form description (optional)"
            />
          </Form.Item>

          <Form.Item label="Assign to Desk *" required>
            <Select
              value={selectedDeskForForm}
              onChange={(value) => {
                console.log("📌 Desk selected:", value);
                setSelectedDeskForForm(value);
              }}
              placeholder="Select a desk"
              style={{ width: '100%' }}
            >
              {desksList.map(desk => (
                <Option key={desk.id} value={desk.id}>
                  <DesktopOutlined style={{ marginRight: 8 }} />
                  {desk.name || `Desk ${desk.desk_number}`}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* ========== FORM PREVIEW MODAL ========== */}
      <Modal
        open={showFormPreview}
        onCancel={() => {
          setShowFormPreview(false);
          setPreviewForm(null);
        }}
        title="Form Preview"
        footer={[
          <Button key="close" onClick={() => setShowFormPreview(false)}>Close</Button>,
          previewForm?.status === 'pending' && (
            <>
              <Button key="reject" danger onClick={() => handleUpdateFormStatus(previewForm.id, 'rejected')} loading={updatingStatus}>
                Reject
              </Button>
              <Button key="approve" type="primary" onClick={() => handleUpdateFormStatus(previewForm.id, 'approved')} style={{ background: "#52c41a" }} loading={updatingStatus}>
                Approve
              </Button>
            </>
          ),
        ]}
        width={600}
      >
        {previewForm && (
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Form ID"><Text code>#{previewForm.id}</Text></Descriptions.Item>
            <Descriptions.Item label="Title">{previewForm.title}</Descriptions.Item>
            <Descriptions.Item label="Description">{previewForm.description || <Text type="secondary">No description</Text>}</Descriptions.Item>
            <Descriptions.Item label="Created By">{previewForm.created_by_name || `User ${previewForm.created_by}`}</Descriptions.Item>
            <Descriptions.Item label="Assigned Desk"><Tag color="blue" icon={<DesktopOutlined />}>Desk {previewForm.assigned_desk}</Tag></Descriptions.Item>
            <Descriptions.Item label="Status">
              <Badge status={previewForm.status === 'approved' ? 'success' : previewForm.status === 'rejected' ? 'error' : 'warning'} text={previewForm.status?.toUpperCase() || 'PENDING'} />
            </Descriptions.Item>
            <Descriptions.Item label="Created At">{previewForm.created_at ? new Date(previewForm.created_at).toLocaleString() : 'N/A'}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* ========== ULB SELECTION MODAL / USERS SELECTION MODAL ========== */}
      <Modal
        open={showModal && modalType === 'ulb'}
        onCancel={() => {
          setShowModal(false);
          setSearchText('');
          setActiveTab('all');
          setSelectedClasses([]);
          setSelectedTypes([]);
          setSelectedItems([]);
          setLoadingError(null);
        }}
        width={1000}
        title={getModalTitle()}
        footer={[
          <Button key="selectAll" onClick={selectAll}>Select All ({ulbs.length})</Button>,
          <Button key="selectFiltered" onClick={selectAllFiltered} disabled={filteredData.length === 0}>Select Filtered ({filteredData.length})</Button>,
          <Button key="clearAll" onClick={clearAll}>Clear</Button>,
          <Button key="cancel" onClick={() => setShowModal(false)}>Cancel</Button>,
          <Button key="save" type="primary" onClick={saveUlbSelections}>Save ({selectedItems.length})</Button>,
        ]}
        maskClosable={false}
        width="90%"
        centered
        bodyStyle={{ 
          padding: '20px',
          maxHeight: '70vh',
          overflowY: 'auto'
        }}
      >
        {loadingData ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}>Loading...</div>
          </div>
        ) : loadingError ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Alert 
              message="Error Loading Data" 
              description={loadingError} 
              type="error" 
              showIcon 
            />
            <Button style={{ marginTop: '16px' }} onClick={() => openUlbModal(currentRole)}>
              Retry
            </Button>
          </div>
        ) : (
          <div>
            <Alert 
              message={
                currentRole?.id === 2 
                  ? "Select District Admin users that this role can access"
                  : currentRole?.id === 3 
                    ? "Select Divisional Officer users that this role can access"
                    : "Select the ULBs this role can access"
              }
              type="info" 
              showIcon 
              style={{ marginBottom: '16px' }} 
            />
            
            {/* Show tabs only for ULB role (id:1) */}
            {(currentRole?.id === 1) && (
              <>
                <Tabs 
                  activeKey={activeTab} 
                  onChange={setActiveTab} 
                  style={{ marginBottom: '16px' }}
                >
                  <TabPane tab={<span><AppstoreOutlined />All ULBs</span>} key="all" />
                  <TabPane tab={<span><FilterOutlined />Select by Class</span>} key="class" />
                  <TabPane tab={<span><FileTextOutlined />Select by Type</span>} key="type" />
                </Tabs>
                
                {activeTab === 'class' && (
                  <Card size="small" style={{ marginBottom: '16px', background: '#fafafa' }}>
                    <Space wrap>
                      <Text strong>Select Classes:</Text>
                      <Button size="small" onClick={selectAllClasses}>Select All</Button>
                      <Button size="small" onClick={clearAllClasses}>Clear</Button>
                    </Space>
                    <div style={{ marginTop: '8px' }}>
                      <Space size={[8, 8]} wrap>
                        {getUniqueClasses().map(className => {
                          const isSelected = selectedClasses.includes(className);
                          const count = ulbs.filter(u => u.class?.toString() === className).length;
                          return (
                            <Tag.CheckableTag
                              key={className}
                              checked={isSelected}
                              onChange={() => toggleClass(className)}
                              style={{ 
                                padding: '4px 12px', 
                                fontSize: '13px',
                                backgroundColor: isSelected ? getClassColor(className) : '#f5f5f5',
                                border: '1px solid #d9d9d9',
                                cursor: 'pointer'
                              }}
                            >
                              Class {className} ({count})
                            </Tag.CheckableTag>
                          );
                        })}
                      </Space>
                    </div>
                  </Card>
                )}
                
                {activeTab === 'type' && (
                  <Card size="small" style={{ marginBottom: '16px', background: '#fafafa' }}>
                    <Space wrap>
                      <Text strong>Select Types:</Text>
                      <Button size="small" onClick={selectAllTypes}>Select All</Button>
                      <Button size="small" onClick={clearAllTypes}>Clear</Button>
                    </Space>
                    <div style={{ marginTop: '8px' }}>
                      <Space size={[8, 8]} wrap>
                        {getUniqueTypes().map(typeName => {
                          const isSelected = selectedTypes.includes(typeName);
                          const count = ulbs.filter(u => u.ulb_type === typeName).length;
                          return (
                            <Tag.CheckableTag
                              key={typeName}
                              checked={isSelected}
                              onChange={() => toggleType(typeName)}
                              style={{ 
                                padding: '4px 12px', 
                                fontSize: '13px',
                                backgroundColor: isSelected ? getTypeColor(typeName) : '#f5f5f5',
                                border: '1px solid #d9d9d9',
                                cursor: 'pointer'
                              }}
                            >
                              {typeName} ({count})
                            </Tag.CheckableTag>
                          );
                        })}
                      </Space>
                    </div>
                  </Card>
                )}
              </>
            )}
            
            <Search
              placeholder={currentRole?.id === 1 ? "Search by ULB name, district, division..." : "Search by username, name, email..."}
              allowClear
              enterButton={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ marginBottom: '16px' }}
            />
            
            {/* SCROLLABLE LIST */}
            <div style={{ 
              maxHeight: '400px', 
              overflowY: 'auto',
              border: '1px solid #f0f0f0',
              borderRadius: '8px',
              padding: '8px'
            }}>
              {filteredData.length > 0 ? (
                <List
                  dataSource={filteredData}
                  renderItem={item => {
                    const isSelected = selectedItems.includes(item.id);
                    return (
                      <List.Item
                        key={item.id}
                        onClick={() => toggleItem(item.id)}
                        style={{ 
                          cursor: 'pointer',
                          padding: '12px',
                          backgroundColor: isSelected ? '#e6f7ff' : 'transparent',
                          borderRadius: '6px',
                          marginBottom: '4px',
                          transition: 'all 0.3s ease'
                        }}
                        actions={[
                          <Checkbox 
                            checked={isSelected} 
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => toggleItem(item.id)}
                          />
                        ]}
                      >
                        <List.Item.Meta
                          avatar={<BankOutlined style={{ color: '#722ed1', fontSize: '20px' }} />}
                          title={
                            <span style={{ fontWeight: 600, fontSize: '14px' }}>
                              {currentRole?.id === 1 ? (item.ulb_name || item.name) : (item.full_name || item.username)}
                            </span>
                          }
                          description={
                            currentRole?.id === 1 ? (
                              <Space size={[8, 4]} wrap style={{ marginTop: '4px' }}>
                                <Tag color="default" style={{ fontSize: '11px' }}>ID: {item.id}</Tag>
                                {getDivisionName(item.division_id) !== 'N/A' && (
                                  <Tag color="orange" style={{ fontSize: '11px' }}>
                                    <GlobalOutlined /> {getDivisionName(item.division_id)}
                                  </Tag>
                                )}
                                {getDistrictName(item.district_id) !== 'N/A' && (
                                  <Tag color="green" style={{ fontSize: '11px' }}>
                                    <EnvironmentOutlined /> {getDistrictName(item.district_id)}
                                  </Tag>
                                )}
                                {item.class && (
                                  <Tag color={getClassColor(item.class)} style={{ fontSize: '11px' }}>
                                    Class {item.class}
                                  </Tag>
                                )}
                                {item.ulb_type && (
                                  <Tag color={getTypeColor(item.ulb_type)} style={{ fontSize: '11px' }}>
                                    {item.ulb_type}
                                  </Tag>
                                )}
                              </Space>
                            ) : (
                              <Space size={[8, 4]} wrap style={{ marginTop: '4px' }}>
                                <Tag color="blue" style={{ fontSize: '11px' }}>Username: {item.username}</Tag>
                                <Tag color="cyan" style={{ fontSize: '11px' }}>Email: {item.email || 'N/A'}</Tag>
                                {item.designation && (
                                  <Tag color="purple" style={{ fontSize: '11px' }}>{item.designation}</Tag>
                                )}
                                <Tag color={item.status === 'active' ? 'green' : 'orange'} style={{ fontSize: '11px' }}>
                                  {item.status || 'active'}
                                </Tag>
                              </Space>
                            )
                          }
                        />
                      </List.Item>
                    );
                  }}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                  <SearchOutlined style={{ fontSize: 48, color: '#ccc', marginBottom: '16px' }} />
                  <div>
                    <Text type="secondary" style={{ fontSize: '14px' }}>No results found</Text>
                  </div>
                  {searchText && (
                    <Button 
                      size="small" 
                      style={{ marginTop: '12px' }}
                      onClick={() => setSearchText('')}
                    >
                      Clear Search
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
      
      {/* ========== DESK SELECTION MODAL - FULLY SCROLLABLE ========== */}
      <Modal
        open={showModal && modalType === 'desk'}
        onCancel={() => {
          setShowModal(false);
          setSelectedDesks([]);
        }}
        width={500}
        title="Select Desks"
        footer={[
          <Button key="selectAll" onClick={selectAllDesks}>Select All Desks</Button>,
          <Button key="clearAll" onClick={clearAllDesks}>Clear All</Button>,
          <Button key="cancel" onClick={() => setShowModal(false)}>Cancel</Button>,
          <Button key="save" type="primary" onClick={saveDeskSelections}>Save Desks ({selectedDesks.length})</Button>,
        ]}
        bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', padding: '20px' }}
      >
        <Card style={{ background: '#f9f9f9' }}>
          <Row gutter={[16, 16]}>
            {deskOptions.map(desk => {
              const isSelected = selectedDesks.includes(desk);
              return (
                <Col span={12} key={desk}>
                  <div onClick={() => toggleDesk(desk)} style={{
                    padding: '12px',
                    border: `1px solid ${isSelected ? '#722ed1' : '#d9d9d9'}`,
                    borderRadius: '8px',
                    backgroundColor: isSelected ? '#f9f0ff' : 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <Space>
                      <DesktopOutlined style={{ color: isSelected ? '#722ed1' : '#999' }} />
                      <Text strong>Desk {desk}</Text>
                    </Space>
                    <Checkbox checked={isSelected} />
                  </div>
                </Col>
              );
            })}
          </Row>
        </Card>
      </Modal>
    </div>
  );
};

export default FormAccessControl;